"use client";

import ErrorMessage from "@/components/common/error-message";
import LoadingText from "@/components/common/loading-text";
import useCommunityAndChannelParams from "@/hooks/useChannelAndCommunityParams";
import { COMMUNITY_MEMBERS_API } from "@/lib/apis";
import { formatViews } from "@/lib/utils";
import { IGetCommunityMembersResponseDTO } from "@/types/community";
import { useInfiniteQuery } from "@tanstack/react-query";
import { Loader2Icon } from "lucide-react";
import { useMemo } from "react";
import CommunityMemberCard from "./members-card";

const fetchMembers = async ({
	communitySlug,
	channelSlug,
	pageParam = 1,
	perPageParam = 10,
}: {
	communitySlug: string;
	channelSlug?: string;
	pageParam: number;
	perPageParam?: number;
}) => {
	const url = new URL(`${COMMUNITY_MEMBERS_API}`);
	url.searchParams.set("page", pageParam.toString());
	url.searchParams.set("perPage", perPageParam.toString());
	url.searchParams.set("community-slug", communitySlug);

	if (channelSlug) {
		url.searchParams.set("channel-slug", channelSlug);
	}

	const response = await fetch(url);
	const data = await response.json();

	return data;
};

const AllCommunityMembers = () => {
	const { communitySlug, channelSlug } = useCommunityAndChannelParams();

	const { hasNextPage, fetchNextPage, isFetchingNextPage, isLoading, isError, data } =
		useInfiniteQuery<IGetCommunityMembersResponseDTO>({
			queryKey: ["all-community-members", communitySlug, channelSlug],
			queryFn: ({ pageParam }) =>
				fetchMembers({
					communitySlug,
					channelSlug,
					pageParam,
					perPageParam: 9,
				}),
			getNextPageParam: (lastPage) =>
				lastPage.pagination?.page ? lastPage.pagination?.page + 1 : undefined,
		});

	const mergedMembers = useMemo(() => {
		const formattedMembers: IGetCommunityMembersResponseDTO["data"]["members"] = [];

		data?.pages?.forEach((page) => {
			if (page?.data) {
				page?.data?.members?.forEach((member) => formattedMembers.push(member));
			}
		});

		return formattedMembers;
	}, [data?.pages]);

	const totalMembersCount = useMemo(
		() => formatViews(data?.pages?.[0]?.pagination?.total || 0),
		[data]
	);

	return (
		<section>
			<h3 className="text-2xl font-bold">
				Community Members {isLoading ? "..." : `(${totalMembersCount})`}
			</h3>

			<div className="mt-6 grid gap-4">
				{isLoading ? (
					<LoadingText text="Fetching community members" />
				) : isError ? (
					<ErrorMessage content="Could not fetch community members" />
				) : mergedMembers.length > 0 ? (
					<div className="grid gap-4 md:grid-cols-3">
						{mergedMembers.map((member, i) => (
							<CommunityMemberCard key={`${member.username}_${i}`} member={member} />
						))}
					</div>
				) : (
					<span className="text-sm text-gray-400">No members</span>
				)}
			</div>

			<div className="mt-6">
				{!isLoading && !hasNextPage && mergedMembers.length ? (
					<div className="flex items-center justify-center gap-x-1 text-sm font-medium text-gray-400">
						<span>You have reached the end.</span>
					</div>
				) : !isLoading && hasNextPage ? (
					<button
						onClick={() => fetchNextPage()}
						className="mx-auto block max-h-14 w-fit rounded-lg bg-white px-6 py-3 text-center text-sm font-medium text-gray-900 disabled:opacity-40 md:text-lg"
						disabled={isFetchingNextPage}
					>
						{isFetchingNextPage ? (
							<Loader2Icon className="h-5 w-5 animate-spin stroke-brand-primary" />
						) : (
							"Show more"
						)}
					</button>
				) : null}
			</div>
		</section>
	);
};

export default AllCommunityMembers;
